import { useRef, useEffect, useState } from "react";
import { ChevronDown, ChevronLeft, MoreHorizontal, Send } from "lucide-react";
import CelebrationButton from "../ui/CelebrationButton/CelebrationButton";
import { useSelector } from "react-redux";

/** These stay on the header. Everything else folds into the actions panel. */
const PINNED_LABELS = new Set([
  "back",
  "print",
  "email",
  "copy",
  "dock receipt",
  "hbl",
  "save draft",
]);

const isPinnedAction = (action) => {
  if (!action || action === "|") return false;
  if (action.pinned === true) return true;
  if (action.pinned === false) return false;
  return PINNED_LABELS.has(String(action.label || "").trim().toLowerCase());
};

const splitActions = (actions) => {
  const pinned = [];
  const overflow = [];
  let sep = false;
  actions.forEach((action) => {
    if (action === "|") {
      sep = true;
      return;
    }
    const target = isPinnedAction(action) ? pinned : overflow;
    if (sep && target.length) target.push("|");
    target.push(action);
    sep = false;
  });
  return { pinned, overflow };
};

export default function PageHeader({
  icon,
  title,
  badge,
  subtitle,
  actions = [],
  collapsibleActions = false,
  scrollableActions = false,
  onSubmit,
  submitLabel = 'Submit Booking',
  submitIcon = <Send />,
  submitClassName = 'inline-flex items-center gap-1.5 text-[13px] font-medium px-3.5 h-[38px] rounded-[8px] transition-all duration-150 bg-green-500 text-white hover:bg-green-700',
}) {
  const headerRef = useRef(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const { isAnimate } = useSelector((state) => state.toast);
  const { loader } = useSelector((state) => state.loader);
  const { pinned, overflow } = splitActions(actions);
  const backActions = pinned.filter(
    (action) => action !== "|" && String(action.label || "").trim().toLowerCase() === "back",
  );
  const pinnedGroupKey = (label) => {
    const name = String(label || "").trim().toLowerCase();
    if (name === "print" || name === "email" || name === "copy") return "share";
    if (name === "dock receipt" || name === "hbl") return "docs";
    if (name === "save draft" || name.includes("submit")) return "commit";
    return "other";
  };
  const showCollapse = collapsibleActions && !scrollableActions && overflow.length > 0;
  const showRail = scrollableActions && overflow.length > 0;
  const expanded = showCollapse && actionsOpen;
  const pinnedGroups = ["share", "docs", "other", "commit"]
    .map((key) => pinned.filter(
      (action) => action !== "|" && pinnedGroupKey(action.label) === key && String(action.label || "").trim().toLowerCase() !== "back",
    ))
    .filter((group) => group.length > 0);

  useEffect(() => {
    const update = () => {
      const h = headerRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty(
        "--page-header-height",
        `${h}px`
      );
    };

    update();

    const ro = new ResizeObserver(update);

    if (headerRef.current) ro.observe(headerRef.current);

    return () => ro.disconnect();
  }, [actionsOpen]);

  useEffect(() => {
    // Small timeout ensures DOM is ready before scrolling
    const timer = setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
        const main = document.getElementById('main-content');
        if (main) main.scrollTop = 0;
    }, 0);
    return () => clearTimeout(timer);
}, []);

  const variantMap = {
    ghost:
      "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700",

    tertiary:
      "bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-600",

    black:
      "bg-slate-900 text-white hover:bg-slate-700 dark:bg-slate-600 dark:text-slate-100 dark:hover:bg-slate-500",

    success:
      "bg-green-500 text-white hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-500",
  };

  const renderAction = (action, i, showLabel = false) =>
    action === "|" ? (
      <div
        key={`sep-${i}`}
        className="mx-1.5 h-6 w-px shrink-0 bg-slate-400 dark:bg-slate-400"
      />
    ) : (
      <button
        key={`${action.label || "action"}-${i}`}
        type="button"
        onClick={action.onClick}
        disabled={action.disabled}
        title={action.label}
        className={`
          inline-flex items-center justify-center gap-1 sm:gap-1.5
          text-[12.5px] font-medium
          px-2.5
          h-[36px]
          rounded-[8px]
          transition-all duration-150
          disabled:opacity-40 disabled:cursor-not-allowed
          ${variantMap[action.variant || "tertiary"]}
        `}
      >
        {action.icon && (
          <span className="h-[18px] w-[18px] shrink-0 [&>svg]:h-full [&>svg]:w-full">
            {action.icon}
          </span>
        )}
        {action.label && (
          <span className={`${showLabel ? "inline" : "hidden sm:inline"} whitespace-nowrap`}>
            {action.label}
          </span>
        )}
      </button>
    );

  const groupRule = (
    <span aria-hidden className="mx-1.5 h-6 w-px shrink-0 bg-slate-400 dark:bg-slate-400" />
  );

  return (
    <header
      ref={headerRef}
      className="fixed right-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-200 dark:border-slate-700 shadow-sm"
      style={{
        left: "var(--sidebar-width)",
        top: "var(--navbar-height)",
      }}
    >
      <div className="flex h-[72px] items-center gap-2 px-3 sm:gap-3 sm:px-4">
        <div className={`relative flex h-full flex-1 items-center overflow-hidden ${showRail ? "min-w-[12rem]" : "min-w-0"}`}>
          <div
            className={`flex min-w-0 items-center gap-2 transition-opacity duration-200 sm:gap-3 ${
              expanded ? "pointer-events-none opacity-0" : "opacity-100"
            }`}
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] bg-gradient-to-br from-[#2563EB] to-[#14B8A6] shadow-md sm:h-10 sm:w-10 sm:rounded-[11px] sm:shadow-lg">
              <span className="text-white [&>svg]:h-4 [&>svg]:w-4 sm:[&>svg]:h-5 sm:[&>svg]:w-5">
                {icon}
              </span>
            </div>
            <div className="flex min-w-0 flex-col gap-0.5">
              <div className="flex items-center gap-1.5">
                <h1 className="truncate text-sm font-bold leading-none tracking-tight text-slate-900 dark:text-slate-50 sm:text-base lg:text-xl">
                  {title}
                </h1>
                {badge && (
                  <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-current opacity-60" />
                    {badge}
                  </span>
                )}
              </div>
              {subtitle && (
                <div className="hidden truncate text-xs leading-none text-slate-500 dark:text-slate-400 sm:block sm:text-sm">
                  {subtitle}
                </div>
              )}
            </div>
          </div>

          {showCollapse && (
            <div
              className="absolute inset-y-0 right-0 z-20 overflow-hidden bg-white/95 backdrop-blur-md transition-[width] duration-300 ease-out dark:bg-slate-900/95"
              style={{ width: expanded ? "100%" : "0%" }}
            >
              <div className="absolute left-0 top-0 flex h-full items-center pl-1">
                {backActions.map((action, i) => renderAction(action, `back-open-${i}`))}
              </div>
              <div className="flex h-full items-center justify-end gap-1.5 whitespace-nowrap pl-24 pr-1">
                {overflow.map((action, i) => renderAction(action, i, true))}
              </div>
            </div>
          )}
        </div>

        <div className={`flex items-center gap-1 sm:gap-1.5 ${showRail ? "min-w-0 shrink" : "shrink-0"}`}>
          {showRail ? (
            <>
              {/* Back — always first */}
              <div className="flex shrink-0 items-center gap-1 sm:gap-1.5">
                {backActions.map((action, i) => renderAction(action, `back-${i}`))}
              </div>

              {/* More — right after Back */}
              <button
                type="button"
                aria-expanded={actionsOpen}
                aria-haspopup="true"
                title={actionsOpen ? "Hide actions" : "Show more actions"}
                onClick={() => setActionsOpen((open) => !open)}
                className={`group inline-flex h-[36px] shrink-0 items-center gap-1.5 rounded-[8px] border px-3 text-[12.5px] font-semibold transition-all duration-150 ${
                  actionsOpen
                    ? "border-blue-300 bg-blue-50 text-blue-700 shadow-sm dark:border-blue-700 dark:bg-blue-950/60 dark:text-blue-200"
                    : "border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50/60 hover:text-blue-700 hover:shadow-sm dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-blue-700 dark:hover:bg-slate-700 dark:hover:text-blue-200"
                }`}
              >
                <MoreHorizontal
                  className="h-4 w-4 shrink-0"
                  strokeWidth={2.4}
                  aria-hidden
                />
                <span className="whitespace-nowrap">More actions</span>
                <span className={`grid h-[18px] min-w-[18px] place-items-center rounded-full px-1 text-[10px] font-bold leading-none transition-colors ${
                  actionsOpen
                    ? "bg-blue-600 text-white"
                    : "bg-blue-100 text-blue-700 group-hover:bg-blue-200 dark:bg-slate-600 dark:text-slate-100"
                }`}>
                  {overflow.filter((action) => action !== "|").length}
                </span>
                <ChevronDown
                  className={`h-3.5 w-3.5 shrink-0 transition-transform duration-200 ${actionsOpen ? "rotate-180" : ""}`}
                  strokeWidth={2.4}
                  aria-hidden
                />
              </button>

              {/* Remaining pinned actions (not Back / Save Draft / Submit) */}
              <div className="flex shrink-0 items-center gap-1 sm:gap-1.5">
                {pinned
                  .filter((action) => {
                    if (action === "|") return false;
                    const name = String(action.label || "").trim().toLowerCase();
                    return (
                      name !== "back"
                      && name !== "save draft"
                      && !name.includes("submit")
                    );
                  })
                  .map((action, i) => renderAction(action, `pin-${i}`))}
              </div>
              <div className="flex shrink-0 items-center gap-1 sm:gap-1.5">
                {pinned
                  .filter((action) => {
                    if (action === "|") return false;
                    const name = String(action.label || "").trim().toLowerCase();
                    return name === "save draft" || name.includes("submit");
                  })
                  .map((action, i) => renderAction(action, `commit-${i}`))}
              </div>
              {onSubmit && (
                <span className="shrink-0">
                  <CelebrationButton
                    onClick={onSubmit}
                    label={submitLabel}
                    isSuccess={isAnimate}
                    isLoading={loader}
                    className={submitClassName}
                    icon={
                      <span className="h-[18px] w-[18px] shrink-0 [&>svg]:h-full [&>svg]:w-full">
                        {submitIcon}
                      </span>
                    }
                  />
                </span>
              )}
            </>
          ) : showCollapse ? (
            <>
          {!expanded && backActions.map((action, i) => renderAction(action, `back-${i}`))}

          <button
              type="button"
              onClick={() => setActionsOpen((open) => !open)}
              aria-expanded={expanded}
              aria-label={expanded ? "Show heading" : "Show more actions"}
              className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition-colors duration-200 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <ChevronLeft
                className={`h-4 w-4 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
                strokeWidth={2.4}
                aria-hidden
              />
            </button>

          {pinnedGroups.map((group, groupIndex) => (
            <div key={pinnedGroupKey(group[0].label)} className="flex items-center gap-1">
              {groupIndex > 0 && groupRule}
              {group.map((action, i) => renderAction(action, `${groupIndex}-${i}`))}
              {pinnedGroupKey(group[0].label) === "commit" && onSubmit && (
                <CelebrationButton
                  onClick={onSubmit}
                  label={submitLabel}
                  isSuccess={isAnimate}
                  isLoading={loader}
                  className={submitClassName}
                  icon={
                    <span className="h-[18px] w-[18px] shrink-0 [&>svg]:h-full [&>svg]:w-full">
                      {submitIcon}
                    </span>
                  }
                />
              )}
            </div>
          ))}

          {onSubmit && !pinnedGroups.some((group) => pinnedGroupKey(group[0].label) === "commit") && (
            <>
              {pinnedGroups.length > 0 && groupRule}
              <CelebrationButton
                onClick={onSubmit}
                label={submitLabel}
                isSuccess={isAnimate}
                isLoading={loader}
                className={submitClassName}
                icon={
                  <span className="h-[18px] w-[18px] shrink-0 [&>svg]:h-full [&>svg]:w-full">
                    {submitIcon}
                  </span>
                }
              />
            </>
          )}
            </>
          ) : (
            <>
              {actions.map((action, i) => renderAction(action, i))}
              {onSubmit && (
                <CelebrationButton
                  onClick={onSubmit}
                  label={submitLabel}
                  isSuccess={isAnimate}
                  isLoading={loader}
                  className={submitClassName}
                  icon={
                    <span className="h-[18px] w-[18px] shrink-0 [&>svg]:h-full [&>svg]:w-full">
                      {submitIcon}
                    </span>
                  }
                />
              )}
            </>
          )}
        </div>
      </div>
      {showRail && (
        <div
          className={`grid transition-[grid-template-rows] duration-200 ease-out ${
            actionsOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          }`}
        >
          <div className="overflow-hidden">
            <div className="scrollbar-hide flex h-[52px] items-center overflow-x-auto border-t border-slate-200/80 px-3 dark:border-slate-700/80 sm:px-4">
              <div className="ml-auto flex shrink-0 items-center gap-1.5">
                {overflow.map((action, i) => renderAction(action, `extra-${i}`, true))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}